import { ImageTuningValues } from 'business/models/ImageTuningModel';
import { GetCaptureProfileTuningValues } from 'utils/Global';
import getTunedImage, {
  GetHistogramFromCompositeImage,
  calculateLevelingWindow,
  getTunedImageForDrop,
  getTuningWindowForDrop,
  histogramSmoothing,
} from './ImageTuningManager';
import { ExperimentTuningValuesDTO } from 'business/models/Models';
import { RMRectangle } from 'business/models/CustomModels';
import { getRegionIdCached, getWellImageParams } from 'components/well/utils';
import { getWellDropImageUniqueKey } from 'redux/utils/well';

export function getTunedImageForPlate(
  tuningValues: ImageTuningValues,
  orgImage,
  captureProfileID: number,
  quality = 0.5,
) {
  const imageTuningVM = getTuningWindowForPlate(tuningValues, orgImage, captureProfileID);
  let lowerLevelLimit, upperLevelLimit;

  if (imageTuningVM === undefined) {
    return undefined;
  }

  if (imageTuningVM.isAutoLevelingOn == true) {
    lowerLevelLimit = imageTuningVM.autoLevelingLowerLimit;
    upperLevelLimit = imageTuningVM.autoLevelingUpperLimit;
  } else {
    lowerLevelLimit = imageTuningVM.lowerLevelLimit;
    upperLevelLimit = imageTuningVM.upperLevelLimit;
  }

  if (lowerLevelLimit == undefined || upperLevelLimit == undefined) {
    return undefined;
  }

  const tunedImage = getTunedImage(
    orgImage,
    lowerLevelLimit,
    upperLevelLimit,
    'image/png',
    quality,
  );
  return tunedImage;
}

export function getTuningWindowForPlate(
  tuningValues: ImageTuningValues,
  orgImage,
  cpId: number,
) {
  if (!tuningValues) {
    tuningValues = GetCaptureProfileTuningValues()?.find(
      (tv) => tv.captureProfileID == cpId,
    );
  }

  if (tuningValues && cpId && orgImage) {
    const imageTuningVM: ImageTuningValues = {};
    if (tuningValues.isAutoLevelingOn) {
      let histogramValues = GetHistogramFromCompositeImage(orgImage);
      histogramValues = histogramSmoothing(histogramValues);
      if (histogramValues != null) {
        const window = calculateLevelingWindow(
          histogramValues,
          tuningValues.autoLevelingLowThreshold,
          tuningValues.autoLevelingHighThreshold,
        );
        if (window) {
          imageTuningVM.autoLevelingLowerLimit = window[0];
          imageTuningVM.autoLevelingUpperLimit = window[1];
          imageTuningVM.lowerLevelLimit = window[0];
          imageTuningVM.upperLevelLimit = window[1];
          imageTuningVM.isAutoLevelingOn = true;
        }
      }
    } else {
      imageTuningVM.lowerLevelLimit = tuningValues.lowerLevelLimit;
      imageTuningVM.upperLevelLimit = tuningValues.upperLevelLimit;
      imageTuningVM.isAutoLevelingOn = tuningValues.isAutoLevelingOn;
    }
    return imageTuningVM;
  }
  return undefined;
}

export const getTunedWellDropImage = (
  experimentTuningValues: ExperimentTuningValuesDTO[],
  img,
  cpID,
  captureResultTuningValuesDto,
  onTuningCompletion,
) => {
  const tuningWindowVM = getTuningWindowForDrop(
    experimentTuningValues,
    img,
    cpID,
    captureResultTuningValuesDto,
    false,
  );

  if (
    tuningWindowVM == undefined ||
    tuningWindowVM.lowerLevelLimit == undefined ||
    tuningWindowVM.upperLevelLimit == undefined
  ) {
    return img;
  }

  const tunedImage = getTunedImageForDrop(
    img,
    tuningWindowVM.isAutoLevelingOn,
    tuningWindowVM.autoLevelingLowerLimit,
    tuningWindowVM.autoLevelingUpperLimit,
    tuningWindowVM.lowerLevelLimit,
    tuningWindowVM.upperLevelLimit,
    onTuningCompletion,
    tuningWindowVM,
  );

  return tunedImage;
};

export const getCroppedImages = async (
  fullImage: HTMLImageElement,
  wellNumbers: number[],
  wellRects: RMRectangle[],
  plateCoordinates:
    | { startX: number; startY: number; plateWidth: number; plateHeight: number }
    | undefined = undefined,
): Promise<{ [wellNum: number]: HTMLImageElement }> => {
  const result: { [wellNum: number]: HTMLImageElement } = {};
  wellNumbers?.forEach((wellNum) => {
    const rect = wellRects[wellNum - 1];
    result[wellNum] = cropWithKnownDisplaySize(
      fullImage,
      {
        width: plateCoordinates.plateWidth,
        height: plateCoordinates.plateHeight,
        startX: plateCoordinates.startX,
        startY: plateCoordinates.startY,
      },
      rect,
    );
  });
  await loadAllImages(result);
  return result;
};

export function cropWithKnownDisplaySize(originalImage, displaySize, cropFromDisplay) {
  const {
    width: displayW,
    height: displayH,
    startX: startX,
    startY: startY,
  } = displaySize;
  const { x, y, w, h } = cropFromDisplay;

  // Actual pixel size of the original image
  const realW = originalImage.naturalWidth;
  const realH = originalImage.naturalHeight;

  // Scale factor from display → real image
  const scaleX = realW / displayW;
  const scaleY = realH / displayH;

  // Scale crop box to real image size
  const sx = (x - startX) * scaleX;
  const sy = (y - startY) * scaleY;
  const sw = w * scaleX;
  const sh = h * scaleY;

  // Crop it using canvas
  const canvas = document.createElement('canvas');
  const canvasWidth = Math.round(sw);
  const canvasHeight = Math.round(sh);
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  ctx.drawImage(originalImage, sx, sy, sw, sh, 0, 0, canvasWidth, canvasHeight);

  const croppedImg = new Image();
  croppedImg.width = w;
  croppedImg.height = h;
  croppedImg.src = canvas.toDataURL();
  return croppedImg;
}

export function pasteModifiedCropsOnFullImage(
  modifiedCrops,
  wellRects,
  displaySize,
  originalImage,
) {
  const { width: displayW, height: displayH, startX, startY } = displaySize;

  const realW = originalImage.naturalWidth;
  const realH = originalImage.naturalHeight;

  const scaleX = realW / displayW;
  const scaleY = realH / displayH;

  // Create canvas for new full image
  const canvas = document.createElement('canvas');
  canvas.width = realW;
  canvas.height = realH;
  const ctx = canvas.getContext('2d');

  // Draw original full image first
  ctx.drawImage(originalImage, 0, 0, realW, realH);

  // Go through each wellNumber in modifiedCrops
  for (const wellNumber in modifiedCrops) {
    const croppedImage = modifiedCrops[wellNumber];
    const rect = wellRects[parseInt(wellNumber) - 1];

    if (!rect || !croppedImage) {
      continue;
    }

    const sx = (rect.x - startX) * scaleX;
    const sy = (rect.y - startY) * scaleY;
    const sw = rect.w * scaleX;
    const sh = rect.h * scaleY;

    ctx.drawImage(
      croppedImage,
      0,
      0,
      croppedImage.naturalWidth,
      croppedImage.naturalHeight,
      sx,
      sy,
      sw,
      sh,
    );
  }

  const resultImage = new Image();
  resultImage.src = canvas.toDataURL();
  return resultImage;
}

export const loadAllImages = (images: { [wellNum: number]: HTMLImageElement }) => {
  const promises = Object.values(images).map(
    (img) =>
      new Promise<HTMLImageElement>((resolve) => {
        if (img.complete) {
          resolve(img); // already loaded
        } else {
          img.onload = () => resolve(img);
        }
      }),
  );
  return Promise.all(promises);
};

export const getTunedImages = async (
  tuningAppliedWellNumbers,
  croppedImages,
  wellDropMetaData,
  wellDetails,
) => {
  const tunedImages: { [wellNum: number]: any } = {};
  tuningAppliedWellNumbers?.forEach((wellNum) => {
    if (croppedImages[wellNum]) {
      const regionID = getRegionIdCached(
        wellDetails.plateID,
        wellNum,
        wellDetails.dropNumber,
        wellDetails.regionName,
        wellDetails.regions,
      );
      const imageKey = getWellDropImageUniqueKey(
        getWellImageParams({ ...wellDetails, wellNum, regionID }),
      );
      const _meta = wellDropMetaData?.[imageKey];
      if (_meta && _meta.captureResultTuningValuesDto) {
        const tuningValues = _meta?.captureResultTuningValuesDto;

        tunedImages[wellNum] = getTunedImageForDrop(
          croppedImages[wellNum],
          false,
          0,
          0,
          tuningValues.lowerLevelLimit,
          tuningValues.upperLevelLimit,
          undefined,
          undefined,
          1,
        );
      }
    }
  });
  await loadAllImages(tunedImages);
  return tunedImages;
};

export const waitForValue = async ({ action, timeout = 10000, interval = 200 }) => {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    const value = action();
    if (value) {
      return value;
    }

    await new Promise((res) => setTimeout(res, interval));
  }
};
